import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React from 'react';

import {
  CORNER_CELL_SX,
  HEADER_CELL_SX,
  STATUS_COLUMN_WIDTH,
} from './ServiceActivityMatrix.styles';
import { ActivitySort, ActivitySortKey } from '../../../@types/service-activity.type';
import { ACTIVITY_STATUS_COLUMNS } from '../../../constants/service-activity.constant';

export interface MatrixHeaderRowProps {
  sort: ActivitySort | null;
  onSort: (key: ActivitySortKey) => void;
  /** Column-header click drills to that status group across every client. */
  onColumnSelect: (statusGroup: string) => void;
  canDrillDown: boolean;
}

/**
 * `aria-sort` belongs on the sorted column only — the ARIA authoring practices
 * put it on one header at a time. Returning `undefined` omits the attribute so
 * screen readers don't announce "not sorted" on all twelve headers.
 */
const ariaSortFor = (sort: ActivitySort | null, key: ActivitySortKey) => {
  if (sort?.key !== key) return undefined;
  return sort.direction === 'asc' ? ('ascending' as const) : ('descending' as const);
};

/** Spoken state for the sort button's own label, which always needs wording. */
const sortStateLabel = (sort: ActivitySort | null, key: ActivitySortKey) =>
  ariaSortFor(sort, key) ?? 'not sorted';

const HEADER_BUTTON_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  width: '100%',
  height: 40,
  border: 'none',
  bgcolor: 'transparent',
  font: 'inherit',
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: 'text.primary',
  cursor: 'pointer',
  px: '2px',
  lineHeight: 1.15,
  '&:hover': { bgcolor: 'v4.tableHover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
};

export const MatrixHeaderRow: React.FC<MatrixHeaderRowProps> = ({
  sort,
  onSort,
  onColumnSelect,
  canDrillDown,
}) => (
  <TableHead>
    <TableRow>
      <TableCell
        component="th"
        scope="col"
        sx={CORNER_CELL_SX}
        aria-sort={ariaSortFor(sort, 'label')}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onSort('label')}
          aria-label={`Sort by client and job type, ${sortStateLabel(sort, 'label')}`}
          sx={{ ...HEADER_BUTTON_SX, justifyContent: 'flex-start' }}
        >
          Client / Job type
          {sort?.key === 'label' &&
            (sort.direction === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 11 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 11 }} />
            ))}
        </Box>
      </TableCell>

      {ACTIVITY_STATUS_COLUMNS.map((column) => (
        <TableCell
          key={column.field}
          component="th"
          scope="col"
          sx={{ ...HEADER_CELL_SX, width: STATUS_COLUMN_WIDTH }}
          aria-sort={ariaSortFor(sort, column.field)}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              component="button"
              type="button"
              onClick={() => onSort(column.field)}
              aria-label={`Sort by ${column.header}, ${sortStateLabel(sort, column.field)}`}
              sx={{ ...HEADER_BUTTON_SX, height: 26 }}
            >
              {column.header}
              {sort?.key === column.field &&
                (sort.direction === 'asc' ? (
                  <ArrowUpwardIcon sx={{ fontSize: 11 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 11 }} />
                ))}
            </Box>
            {canDrillDown && (
              <Box
                component="button"
                type="button"
                onClick={() => onColumnSelect(column.group)}
                aria-label={`Open the job list filtered to ${column.header} across all clients`}
                sx={{
                  ...HEADER_BUTTON_SX,
                  height: 13,
                  fontSize: '8px',
                  letterSpacing: '0.04em',
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                }}
              >
                View all
              </Box>
            )}
          </Box>
        </TableCell>
      ))}

      <TableCell
        component="th"
        scope="col"
        sx={{ ...HEADER_CELL_SX, width: STATUS_COLUMN_WIDTH }}
        aria-sort={ariaSortFor(sort, 'total')}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onSort('total')}
          aria-label={`Sort by total, ${sortStateLabel(sort, 'total')}`}
          sx={HEADER_BUTTON_SX}
        >
          Total
          {sort?.key === 'total' &&
            (sort.direction === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 11 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 11 }} />
            ))}
        </Box>
      </TableCell>
    </TableRow>
  </TableHead>
);
